<template>
  <div class="map-chart" :style="{width: config.chartWidth+'px',height:config.chartHeight+'px'}">
    <div class="map-chart-wrapper">
      <img
        v-if="borderImgUrl" class="map-chart-border" :src="borderImgUrl" alt=""
        :style="chartBorderTemplateImageStyle">
      <div class="map-chart-title" :style="titleBarInlineStyle">
        {{ config.chartTitle }}
        <el-tooltip v-if="config.chartViewData.showIndicator" class="item" :open-delay="500" effect="dark" placement="top">
          <div slot="content" v-html="indicatorDescription"></div>
          <i class="el-icon-question"></i>
        </el-tooltip>
      </div>
    </div>
    <div ref="mapChart" class="map-chart-container"></div>
  </div>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
import { COLOR_LIST } from '@/utils/defaultColorData'
import { imgURLtoBase64, randomString, hexToRgba } from '@/utils/common'
import { relateBus } from '@/eventBus'
import { mapBaseOption } from './options'
import ChartMixin from './mixin'
import { MAP_ROUTE_LINE_COLOR } from '@/utils/mapRouteGeo'

export default {
  mixins: [ChartMixin],

  data () {
    return {
      mapName: '',
    }
  },
  computed: {
    chartStyle () {
      return this.config.chartSet.chartStyle
    },

    // 指标库/API 回填为 { x, y_0, label? }[]，无表头行、无 col_*；自定义表格数据为 [表头, 数据行...]
    isIndicatorApiChartData () {
      const cd = this.chartData
      if (!cd?.length || !cd[0]) return false
      const first = cd[0]
      return !('col_0' in first) && (first.x !== undefined || first.y_0 !== undefined)
    },
    columnName () {
      const list = []
      if (!this.chartData?.length) return []
      if (this.isIndicatorApiChartData) {
        // 与 mixin 回填一致：value 为 [ y, label ]（地区名 + 指标说明文案）
        return ['地区', '说明']
      }
      const colKeyList = Object.keys(this.chartData[0])
      colKeyList.forEach(item => {
        if (this.chartData[1][item] !== '' && this.chartData[1][item] !== undefined) {
          list.push(this.chartData[0][item])
        }
      })
      return list.slice(1)
    },
    chartData () {
      return this.config.chartViewData.chartData
    },
    renderData () {
      if (!this.chartData?.length || !Array.isArray(this.chartData)) {
        return []
      }
      const list = []
      const rows = this.isIndicatorApiChartData ? this.chartData : this.chartData.slice(1)
      rows.forEach(item => {
        const value = []
        if (item.x) {
          let curName
          if (this.config.chartSet.chartStyle.map.length === 1) {
            curName = this.mapNameList.find(bar => item.x.indexOf(bar) !== -1)
          }
          list.push({
            name: curName || item.x,
            value: [item.y_0, item.label],
          })
        } else {
          this.columnName.forEach((name, index) => {
            value.push(item[`col_${index + 1}`])
          })
          list.push({
            name: item.col_0 || item.x,
            value,
          })
        }
      })
      return list.length ? list : []
    },
    borderStyle () {
      return this.config.chartSet.borderStyle.borderTemplate
    },
    style () {
      return this.config.chartSet
    },
    chartSet () {
      return this.config.chartSet
    },
  },
  watch: {
    // 配置项变更
    chartSet: {
      handler: function (val) {
        this.init()
      },
      deep: true,
    },
    'config.chartViewData.chartData': {
      async handler () {
        this.init()
      },
    },
    'config.chartViewData.mapRouteOverlay': {
      handler () {
        this.init()
      },
      deep: true,
    },
    'config.chartSet.chartStyle.map': {
      async handler () {
        await this.getMap()
        this.init()
      },
    },
    'config.chartWidth': {
      async handler () {
        this.init()
      },
    },
    'config.chartHeight': {
      async handler () {
        this.init()
      },
    },

    // 全局样式监听
    resolvedChartColorsLabel () {
      this.init()
    },
    async borderStyle (val) {
      if (val) {
        this.borderImgUrl = await imgURLtoBase64(val)
      } else {
        this.borderImgUrl = ''
      }
    },
  },

  async created () {
    if (this.borderStyle) {
      this.borderImgUrl = await imgURLtoBase64(this.borderStyle)
    }
    //  新增地图缩放、轮播延时
    if (!this.chartStyle?.zoom) { this.$set(this.config.chartSet.chartStyle, 'zoom', 1) }
    if (!this.style.chartStyle?.carouselDelay) { this.$set(this.config.chartSet.chartStyle, 'carouselDelay', 3) }
  },
  async mounted () {
    if (!this.charts) {
      this.charts = echarts.init(this.$refs.mapChart)
    }
    await this.getMap()
    this.init(this.showData)
  },
  updated (val) {
  },
  methods: {
    async getMap () {
      // 注册地图类型
      const mapRes = await fetch(`https://kylin-static.oss-cn-hangzhou.aliyuncs.com/common/geojson/${this.config.chartSet.chartStyle.map.join('/')}.json`)
      const mapJson = await mapRes.json()
      this.mapName = randomString()
      echarts.registerMap(this.mapName, mapJson)
      if (this.config.chartSet.chartStyle?.map.length === 1 && !this.mapNameList?.length) { // 长度为1 表示是全国地图
        this.mapNameList = mapJson.features.map(bar => {
          return bar.properties.name
        })
      }
    },

    /** 地图主 series + 航线/散点的 tooltip */
    formatMapSeriesTooltip (params) {
      if (params.seriesType === 'lines') {
        return '航线（起点→落点）'
      }
      if (params.seriesType === 'scatter' && params.data?.value) {
        const v = params.data.value
        if (Array.isArray(v) && v.length >= 2) {
          return `${params.name}<br/>经度: ${v[0]}<br/>纬度: ${v[1]}`
        }
      }
      const d = params.data
      if (!d) return params.name || ''
      // 指标库：{ x, y→y_0, label } → renderData.value = [ y, label ]，轮播/悬停展示 y 与 label
      if (this.isIndicatorApiChartData && Array.isArray(d.value)) {
        const yDisp = d.value[0]
        const labelDisp = d.value[1]
        const parts = []
        if (yDisp !== undefined && yDisp !== null && yDisp !== '') parts.push(String(yDisp))
        if (labelDisp !== undefined && labelDisp !== null && labelDisp !== '') parts.push(String(labelDisp))
        return parts.join('<br/>') || String(d.name || params.name || '')
      }
      if (Array.isArray(d.value) && this.columnName?.length) {
        let str = `${d.name != null ? d.name : ''}<br/>`
        this.columnName.forEach((item, index) => {
          const val = d.value[index]
          str += `${item}:  ${val !== undefined && val !== null && val !== '' ? val : '-'}<br>`
        })
        return str
      }
      return params.name != null ? String(params.name) : ''
    },

    buildRouteGeoRouteSeries () {
      const overlay = this.config.chartViewData?.mapRouteOverlay
      if (!overlay || (!overlay.scatter?.length && !overlay.lines?.length)) return []
      const palette = COLOR_LIST.filter(item => { return item.label === this.resolvedChartColorsLabel })[0]?.value || ['#0276FA', '#3ECB6F']
      const accent = palette[1] || palette[0]
      const extra = []
      if (overlay.scatter?.length) {
        extra.push({
          name: '落点',
          type: 'scatter',
          coordinateSystem: 'geo',
          geoIndex: 0,
          zlevel: 2,
          symbol: 'pin',
          symbolSize: 9,
          itemStyle: { color: accent },
          data: overlay.scatter.map((d, i) => ({
            name: d.name || `落点${i + 1}`,
            value: d.value,
          })),
        })
      }
      if (overlay.lines?.length) {
        extra.push({
          name: '航线',
          type: 'lines',
          coordinateSystem: 'geo',
          geoIndex: 0,
          zlevel: 3,
          polyline: false,
          effect: {
            show: true,
            // 用恒定像素速度代替 period，避免每圈结束瞬间顿挫；箭头改小、尾迹缩短减轻绘制负担
            constantSpeed: 48,
            trailLength: 0.35,
            symbol: 'arrow',
            symbolSize: [4, 5],
            color: MAP_ROUTE_LINE_COLOR,
          },
          lineStyle: {
            width: 1.2,
            opacity: 0.92,
            curveness: 0.12,
            color: MAP_ROUTE_LINE_COLOR,
          },
          data: overlay.lines.map(d => ({
            coords: d.coords,
            lineStyle: { ...d.lineStyle, color: MAP_ROUTE_LINE_COLOR },
          })),
        })
      }
      return extra
    },

    async init () {
      if (!this.mapName) return
      const mapSeries = {
        ..._.cloneDeep(mapBaseOption.series[0]),
        data: this.renderData,
        // hover 仅高亮区域，不浮出国家/地区名称（仍可用 tooltip 看数据）
        emphasis: {
          label: { show: false },
        },
      }
      this.option = _.merge(_.cloneDeep(mapBaseOption), {
        tooltip: {
          textStyle: {
            fontSize: this.chartStyle.fontSize,
          },
          formatter: (params) => this.formatMapSeriesTooltip(params),
        },
        series: [
          mapSeries,
          ...this.buildRouteGeoRouteSeries(),
        ],
        geo: {
          zoom: this.chartStyle.zoom,
          label: {
            show: this.chartStyle.isLabel,
            fontSize: this.chartStyle.fontSize,
          },
          map: this.mapName,
          emphasis: {
            label: {
              show: false,
              fontSize: this.chartStyle.fontSize,
            },
            itemStyle: {
              areaColor: COLOR_LIST.filter(item => { return item.label === this.resolvedChartColorsLabel })[0].value[0], // 鼠标滑过区域颜色
            },
          },
          itemStyle: {
            borderWidth: 1, // 边际线大小
            borderColor: hexToRgba(COLOR_LIST.filter(item => { return item.label === this.resolvedChartColorsLabel })[0].value[0], 0.8), // 边界线颜色
            areaColor: COLOR_LIST.filter(item => { return item.label === this.resolvedChartColorsLabel })[0].value[0],

            opacity: 0.3,
          },
          regions: this.renderData.map(bar => {
            return {
              name: bar.name,
              itemStyle: {
                show: true,
                areaColor: COLOR_LIST.filter(item => { return item.label === this.resolvedChartColorsLabel })[0].value[0],
                opacity: 0.6,
              },
            }
          }),
        },
      })

      this.$nextTick(() => {
        this.charts.setOption(this.option, true)
        this.charts.resize()
      })

      if (this.chartStyle.carousel && this.renderData.length > 0) {
        this.mouseEvents()
        this.tooltipsActive()
      } else {
        if (this.timer) {
          clearInterval(this.timer)
          this.index = -1
        }
      }
    },

    // 提示轮播
    tooltipsActive () {
      if (this.timer) {
        clearInterval(this.timer)
      }
      const dataLength = this.renderData.length
      if (dataLength <= 0) {
        this.index = -1
        return
      }
      // 用定时器控制高亮
      this.timer = setInterval(() => {
        // 清除之前的高亮
        this.charts.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: this.index,
        })
        if (this.index === dataLength - 1) {
          this.index = 0
        } else {
          this.index++
        }
        // 不在此 setOption 全量配置：会打断 lines 的飞线动效导致卡顿

        // 当前下标高亮
        this.charts.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: this.index,
        })
        this.charts.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: this.index,
        })
        // 联动 eventBus 事件触发
        relateBus.$emit('activeChange', this.config.addSn, this.renderData[this.index])
      }, this.config.chartSet.chartStyle.carouselDelay * 1000)
    },

  },
}
</script>

<style lang='scss' scoped>
.map-chart{
  height:100%;
  display: flex;
  flex-direction: column;
  &-wrapper {
    position: relative;
    width:100%;
    margin-bottom: 10px;
    overflow: visible;
  }
  &-border {
    position: absolute;
    top: 0;
    height: 100%;
    z-index: -1;

  }
  &-title {
    padding:7px 30px;
  }

  .map-chart-container{
    flex: 1;
  }
}
</style>
